<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useStore, useStoreGetters } from 'dashboard/composables/store';
import { useLocalizedTime } from 'shared/composables/useLocalizedTime';

import {
  BaseTable,
  BaseTableRow,
  BaseTableCell,
} from 'dashboard/components-next/table';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const { t } = useI18n();
const { dateTime } = useLocalizedTime();
const router = useRouter();
const store = useStore();
const getters = useStoreGetters();

const isLoading = ref(true);

onMounted(async () => {
  // Unified read-only view over two independently paginated sources: the
  // campaigns list is small and always fetched in full (existing backend
  // behaviour); scheduled messages are fetched one page at a time elsewhere
  // in the app, so only the first page is merged in here for now - a known
  // MVP limit until a real unified/paginated backend list exists.
  await Promise.all([
    store.dispatch('campaigns/get'),
    store.dispatch('scheduledMessages/get', 1),
  ]);
  isLoading.value = false;
});

const campaigns = computed(
  () => getters['campaigns/getWhatsAppCampaigns'].value
);
const scheduledMessages = computed(
  () => getters['scheduledMessages/getAllScheduledMessages'].value
);

const CAMPAIGN_STATUS_COLORS = {
  active: 'text-n-blue-11 bg-n-blue-3',
  processing: 'text-n-amber-11 bg-n-amber-3',
  completed: 'text-n-teal-11 bg-n-teal-3',
  failed: 'text-n-ruby-11 bg-n-ruby-3',
};

const SCHEDULED_STATUS_COLORS = {
  pending: 'text-n-blue-11 bg-n-blue-3',
  sent: 'text-n-teal-11 bg-n-teal-3',
  failed: 'text-n-ruby-11 bg-n-ruby-3',
  cancelled: 'text-n-slate-11 bg-n-slate-3',
};

const campaignRows = computed(() =>
  campaigns.value.map(campaign => ({
    type: 'campaign',
    key: `campaign-${campaign.id}`,
    title: campaign.title,
    channel: campaign.inbox?.name,
    statusLabel: t(
      `WHATSAPP_SENDS.CAMPAIGN_STATUS.${campaign.campaign_status.toUpperCase()}`
    ),
    statusColor:
      CAMPAIGN_STATUS_COLORS[campaign.campaign_status] ||
      CAMPAIGN_STATUS_COLORS.active,
    date: campaign.scheduled_at,
    deliveryRate: campaign.delivery_rate,
    raw: campaign,
  }))
);

const scheduledMessageRows = computed(() =>
  scheduledMessages.value.map(message => ({
    type: 'scheduled_message',
    key: `scheduled-${message.id}`,
    title: message.contact?.name,
    channel: message.inbox?.name,
    statusLabel: t(`SCHEDULED_MESSAGES.STATUS.${message.status.toUpperCase()}`),
    statusColor:
      SCHEDULED_STATUS_COLORS[message.status] ||
      SCHEDULED_STATUS_COLORS.pending,
    date: message.scheduled_at,
    deliveryRate: null,
    raw: message,
  }))
);

const typeFilter = ref('all');
const typeFilterOptions = computed(() => [
  { value: 'all', label: t('WHATSAPP_SENDS.FILTER.ALL') },
  { value: 'campaign', label: t('WHATSAPP_SENDS.FILTER.CAMPAIGNS') },
  {
    value: 'scheduled_message',
    label: t('WHATSAPP_SENDS.FILTER.SCHEDULED_MESSAGES'),
  },
]);

const allRows = computed(() => {
  const rows = [...campaignRows.value, ...scheduledMessageRows.value];
  return rows.sort((a, b) => (b.date || 0) - (a.date || 0));
});

const filteredRows = computed(() => {
  if (typeFilter.value === 'all') return allRows.value;
  return allRows.value.filter(row => row.type === typeFilter.value);
});

const typeLabel = row =>
  row.type === 'campaign'
    ? t('WHATSAPP_SENDS.TYPE.CAMPAIGN')
    : t('WHATSAPP_SENDS.TYPE.SCHEDULED_MESSAGE');

const deliveryRateLabel = row =>
  row.deliveryRate === null || row.deliveryRate === undefined
    ? ''
    : `${Math.round(row.deliveryRate * 100)}%`;

const openRow = row => {
  if (row.type === 'campaign') {
    router.push({
      name: 'campaigns_whatsapp_report',
      params: { campaignId: row.raw.id },
    });
  } else {
    router.push({
      name: 'inbox_conversation',
      params: { conversation_id: row.raw.conversation_id },
    });
  }
};

const goToCreateCampaign = () => {
  router.push({ name: 'campaigns_whatsapp_create' });
};

const tableHeaders = computed(() => [
  t('WHATSAPP_SENDS.COLUMNS.TYPE'),
  t('WHATSAPP_SENDS.COLUMNS.NAME'),
  t('WHATSAPP_SENDS.COLUMNS.CHANNEL'),
  t('WHATSAPP_SENDS.COLUMNS.STATUS'),
  t('WHATSAPP_SENDS.COLUMNS.DATE'),
  t('WHATSAPP_SENDS.COLUMNS.DELIVERY_RATE'),
  '',
]);

const actionLabel = row =>
  row.type === 'campaign'
    ? t('WHATSAPP_SENDS.VIEW_REPORT')
    : t('WHATSAPP_SENDS.OPEN_CONVERSATION');
</script>

<template>
  <section class="flex flex-col w-full h-full overflow-hidden bg-n-surface-1">
    <header class="sticky top-0 z-10 px-6">
      <div
        class="flex items-center justify-between w-full max-w-5xl mx-auto h-20"
      >
        <span class="text-heading-1 text-n-slate-12">
          {{ t('WHATSAPP_SENDS.HEADER_TITLE') }}
        </span>
        <Button
          :label="t('WHATSAPP_SENDS.CREATE_BUTTON')"
          color="blue"
          @click="goToCreateCampaign"
        />
      </div>
    </header>
    <main class="flex-1 px-6 overflow-y-auto">
      <div class="grid w-full max-w-5xl gap-4 py-4 mx-auto">
        <div class="w-56">
          <ComboBox v-model="typeFilter" :options="typeFilterOptions" />
        </div>
        <div
          v-if="isLoading"
          class="flex items-center justify-center py-10 text-n-slate-11"
        >
          <Spinner />
        </div>
        <BaseTable
          v-else
          :headers="tableHeaders"
          :items="filteredRows"
          :no-data-message="t('WHATSAPP_SENDS.EMPTY_STATE')"
        >
          <template #row="{ items }">
            <BaseTableRow
              v-for="row in items"
              :key="row.key"
              :item="row"
              class="cursor-pointer"
              @click="openRow(row)"
            >
              <template #default>
                <BaseTableCell>
                  <span class="text-body-main text-n-slate-11">
                    {{ typeLabel(row) }}
                  </span>
                </BaseTableCell>
                <BaseTableCell>
                  <span class="text-body-main text-n-slate-12">
                    {{ row.title }}
                  </span>
                </BaseTableCell>
                <BaseTableCell>
                  <span class="text-body-main text-n-slate-11">
                    {{ row.channel }}
                  </span>
                </BaseTableCell>
                <BaseTableCell>
                  <span
                    class="rounded-full px-2 py-0.5 text-xs font-medium"
                    :class="row.statusColor"
                  >
                    {{ row.statusLabel }}
                  </span>
                  <span
                    v-if="row.raw.dispatch_error"
                    :title="row.raw.dispatch_error"
                    class="block mt-1 text-xs truncate text-n-slate-11 max-w-40"
                  >
                    {{ row.raw.dispatch_error }}
                  </span>
                </BaseTableCell>
                <BaseTableCell>
                  <span
                    class="text-body-main text-n-slate-11 whitespace-nowrap"
                  >
                    {{ dateTime(row.date) }}
                  </span>
                </BaseTableCell>
                <BaseTableCell>
                  <span class="text-body-main text-n-slate-11">
                    {{ deliveryRateLabel(row) }}
                  </span>
                </BaseTableCell>
                <BaseTableCell>
                  <Button
                    :label="actionLabel(row)"
                    slate
                    faded
                    sm
                    @click.stop="openRow(row)"
                  />
                </BaseTableCell>
              </template>
            </BaseTableRow>
          </template>
        </BaseTable>
      </div>
    </main>
  </section>
</template>
